/**
 * Runtime schemas for the PUBLIC catalogue reads in `catalog-api.service.ts`.
 *
 * The shared shapes (brand, category, product, variant, price, spec template)
 * come from `@shared/types`; what is here is the response WRAPPING each public
 * endpoint puts around them, plus `ProductSearchResult`, which only this app
 * declares. Same rules as the shared schemas: loose objects, plain-string ids,
 * `.nullish()` for nullable columns, a `satisfies z.ZodType<Declared>` on each.
 *
 * TOLERANT WHERE THE SHARED SCHEMAS ARE STRICT. The shared schemas also back the
 * admin app, where a missing field should fail loudly. These are the PUBLIC
 * reads, whose callers turn every failure into `[]`/`null`, so a field the
 * server drops or a value it adds would blank a page. Fields the UI does not
 * need read as a fallback instead, and the output types are the declared ones.
 *
 * LOOSENED FIELDS AND THEIR FALLBACKS (absent or `null` unless noted). The
 * tolerance holds only while no screen reads the field, so a screen that starts
 * to must stop relying on the fallback. `catalog-api.loosened-fields.test.ts`
 * fails when it finds a property read (`.field`) of the fields whose names are
 * unique enough to search for (`observationId`, `staleAfter`, `basis`,
 * `attributeType`, `isRequired`). It does not detect destructuring or bracket
 * access, and the other fields are common words and are not covered.
 *   brand:            isActive -> true
 *   price:            variantId, observationId, city, basis, staleAfter -> ""
 *   product:          createdAt -> "", images -> [], specifications -> {},
 *                     tier -> "standard" and status -> "inactive" (an unknown
 *                     value too, with a console.warn)
 *   product extras:   brand, category, variants, prices, specTemplate,
 *                     sourceable -> undefined
 *   image:            type -> null (an unknown value too, with a console.warn),
 *                     alt -> null
 *   variant:          productId -> "", variantAttributes -> {}, images -> []
 *   spec template:    id, categoryId -> "", variantAttributes, attributes -> []
 *   template attr:    id, attributeType -> "", isRequired -> false
 *   template variant attribute: name, type -> ""
 *   category:         displayOrder -> 0, isActive -> true
 *   list wrappers:    categories, brands, variants, compare products -> []
 * `gstRateBp` and `confidence` stay required: the declared type is `number`, so
 * any fallback would look like a real value (a GST-free price), and the
 * backend always sends both (`PriceView`).
 */

import {
	CATALOG_IMAGE_TYPES,
	type CatalogBrand,
	type CatalogCategory,
	type CatalogCategoryTreeNode,
	type CatalogProduct,
	type CatalogProductImage,
	catalogBrandSchema,
	catalogCategorySchema,
	catalogProductImageSchema,
	catalogProductSchema,
	PRODUCT_TIERS,
	type ProductPrice,
	type ProductVariant,
	productPriceSchema,
	productVariantSchema,
	type SpecTemplate,
	specTemplateAttributeSchema,
	specTemplateSchema,
	specTemplateVariantAttributeSchema,
} from "@shared/types";
import { z } from "zod";
import type {
	CatalogFacetCount,
	CatalogFacetField,
	ProductSearchResult,
} from "./catalog-api.service";

/** A list wrapper whose key is missing or `null` reads as empty, as `?? []` did. */
function lenientList<Item extends z.ZodType>(item: Item) {
	return z
		.array(item)
		.nullish()
		.transform((value) => value ?? []);
}

/** A missing or `null` string reads as `""`. */
const textOrEmpty = z
	.string()
	.nullish()
	.transform((value) => value ?? "");

/** Leaves a trace when a tolerated value is replaced, so drift is not silent. */
function warnFallback(field: string, received: unknown, used: unknown): void {
	console.warn(
		`Unexpected ${field} ${JSON.stringify(received) ?? "undefined"}; showing it as ${JSON.stringify(used)}`,
	);
}

/** An optional field the server may send as `null`; `null` reads as absent. */
function absentIfNull<Inner extends z.ZodType>(inner: Inner) {
	return inner.nullish().transform((value) => value ?? undefined);
}

/**
 * The public projection of a brand. `GET /catalog/brands` lists active brands
 * only, and a product's `sourceable` already reflects a withdrawn brand, so a
 * projection that drops `isActive` reads as active.
 */
const publicBrandSchema = catalogBrandSchema.extend({
	isActive: z
		.boolean()
		.nullish()
		.transform((value) => value ?? true),
}) satisfies z.ZodType<CatalogBrand>;

/** A missing or `null` number reads as `0`. */
const numberOrZero = z
	.number()
	.nullish()
	.transform((value) => value ?? 0);

/**
 * `variantId` is compared with a variant's id to pick its price, so an absent
 * one reads as `""`, which matches no variant. `observationId`, `city`, `basis`
 * and `staleAfter` are read by no screen, so they read as `""` rather than
 * failing the page. `amount`, `uom`, `basisLabel`, `observedAt` and `isStale`
 * are printed, and `gstRateBp` and `confidence` are numbers with no value that
 * cannot be mistaken for a real one: all stay required.
 */
const publicPriceSchema = productPriceSchema.extend({
	observationId: textOrEmpty,
	variantId: textOrEmpty,
	city: textOrEmpty,
	basis: textOrEmpty,
	staleAfter: textOrEmpty,
}) satisfies z.ZodType<ProductPrice>;

/**
 * A screen needs the `url`. A caption or kind the server leaves out reads as
 * `null`, and so does a kind this client does not know.
 */
const publicImageSchema = catalogProductImageSchema.extend({
	type: z
		.enum(CATALOG_IMAGE_TYPES)
		.nullish()
		.transform((value) => value ?? null)
		.catch((context) => {
			warnFallback("image type", context.input, null);
			return null;
		}),
	alt: z
		.string()
		.nullish()
		.transform((value) => value ?? null),
}) satisfies z.ZodType<CatalogProductImage>;

/**
 * The product page reads only each attribute's `name`, `unit` and
 * `displayOrder`, so one template that lacks anything else must not blank it.
 * An absent attribute list reads as empty, which `orderedSpecs` handles by
 * falling back to unordered specs.
 */
const publicSpecTemplateAttributeSchema = specTemplateAttributeSchema.extend({
	id: textOrEmpty,
	attributeType: textOrEmpty,
	isRequired: z
		.boolean()
		.nullish()
		.transform((value) => value ?? false),
});

const publicSpecTemplateVariantAttributeSchema =
	specTemplateVariantAttributeSchema.extend({
		name: textOrEmpty,
		type: textOrEmpty,
	});

const publicSpecTemplateSchema = specTemplateSchema.extend({
	id: textOrEmpty,
	categoryId: textOrEmpty,
	variantAttributes: lenientList(publicSpecTemplateVariantAttributeSchema),
	attributes: lenientList(publicSpecTemplateAttributeSchema),
}) satisfies z.ZodType<SpecTemplate>;

/**
 * A category's order and active flag are not read by any screen: the server
 * already sorts, and the lists hold active categories only.
 */
const publicCategorySchema = catalogCategorySchema.extend({
	displayOrder: numberOrZero,
	isActive: z
		.boolean()
		.nullish()
		.transform((value) => value ?? true),
}) satisfies z.ZodType<CatalogCategory>;

/** `children` stays required: the tree is meaningless without it. */
const publicCategoryTreeNodeSchema: z.ZodType<CatalogCategoryTreeNode> = z.lazy(
	() =>
		publicCategorySchema.extend({
			children: z.array(publicCategoryTreeNodeSchema),
		}),
);

const publicVariantSchema = productVariantSchema.extend({
	productId: textOrEmpty,
	variantAttributes: z
		.record(z.string(), z.union([z.string(), z.number()]))
		.nullish()
		.transform((value) => value ?? {}),
	images: lenientList(publicImageSchema),
}) satisfies z.ZodType<ProductVariant>;

/**
 * A tier or status this client does not know reads as the nearest safe value
 * rather than failing the page: `standard` is the style the page already falls
 * back to, and `inactive` claims nothing about the product being on sale.
 */
const publicProductSchema = catalogProductSchema.extend({
	tier: z.enum(PRODUCT_TIERS).catch((context) => {
		warnFallback("product tier", context.input, "standard");
		return "standard";
	}),
	status: z.enum(["draft", "active", "inactive"]).catch((context) => {
		warnFallback("product status", context.input, "inactive");
		return "inactive";
	}),
	images: lenientList(publicImageSchema),
	specifications: z
		.record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
		.nullish()
		.transform((value) => value ?? {}),
	createdAt: textOrEmpty,
	price: publicPriceSchema.nullish(),
	prices: absentIfNull(z.array(publicPriceSchema)),
	brand: absentIfNull(publicBrandSchema),
	variants: absentIfNull(z.array(publicVariantSchema)),
	specTemplate: absentIfNull(publicSpecTemplateSchema),
	category: absentIfNull(publicCategorySchema),
	sourceable: absentIfNull(z.boolean()),
}) satisfies z.ZodType<CatalogProduct>;

/** `GET /catalog/categories` (flat) sends `{ categories }`. */
export const categoryListSchema = z.looseObject({
	categories: lenientList(publicCategorySchema),
});

/** `GET /catalog/categories?tree=true` sends `{ categories }`, nested. */
export const categoryTreeSchema = z.looseObject({
	categories: lenientList(publicCategoryTreeNodeSchema),
});

/** `GET /catalog/brands` sends `{ brands }`. */
export const brandListSchema = z.looseObject({
	brands: lenientList(publicBrandSchema),
});

const facetCountSchema = z.looseObject({
	value: z.string(),
	count: z.number(),
}) satisfies z.ZodType<CatalogFacetCount>;

/**
 * Every facet is optional -- a facet with no matching documents is omitted --
 * and an unknown one passes, so a server that adds a twelfth block does not
 * break search. Spelled out because a record keyed by the enum would REJECT a
 * key outside it.
 */
const facetsSchema = z.looseObject({
	categoryName: z.array(facetCountSchema).optional(),
	brandName: z.array(facetCountSchema).optional(),
	tier: z.array(facetCountSchema).optional(),
	materialFamily: z.array(facetCountSchema).optional(),
	designFamily: z.array(facetCountSchema).optional(),
	finish: z.array(facetCountSchema).optional(),
	colourFamily: z.array(facetCountSchema).optional(),
	sizeLabel: z.array(facetCountSchema).optional(),
	roomTypes: z.array(facetCountSchema).optional(),
	applicationAreas: z.array(facetCountSchema).optional(),
	styleTags: z.array(facetCountSchema).optional(),
}) satisfies z.ZodType<Partial<Record<CatalogFacetField, CatalogFacetCount[]>>>;

/** Fails to compile if `CatalogFacetField` gains a field the schema lacks. */
type MissingFacets = Exclude<
	CatalogFacetField,
	keyof z.output<typeof facetsSchema>
>;
export const facetFieldsCovered: [MissingFacets] extends [never]
	? true
	: never = true;

/** A price the product has none of: `null` whether the key is null or absent. */
const nullablePrice = z
	.number()
	.nullish()
	.transform((value) => value ?? null);

/**
 * A field read out of a search-index document.
 *
 * The first branch of the search endpoint maps Typesense documents with
 * `as string`, so a document indexed before a field existed (`categorySlug`,
 * `variantCount`) arrives without it. Failing the whole search for one stale
 * document would turn a missing label into an empty results page, so these read
 * as empty instead -- which is what the declared type already promises.
 */
const indexedText = z
	.string()
	.nullish()
	.transform((value) => value ?? "");

const searchedProductSchema = z.looseObject({
	id: z.string(),
	name: z.string(),
	// `""` from the search index, absent from the database fallback.
	description: z
		.string()
		.nullish()
		.transform((value) => value ?? undefined),
	brandName: indexedText,
	brandId: indexedText,
	categoryName: indexedText,
	categoryId: indexedText,
	categorySlug: indexedText,
	tier: indexedText,
	uom: indexedText,
	minPrice: nullablePrice,
	maxPrice: nullablePrice,
	hasVariants: z
		.boolean()
		.nullish()
		.transform((value) => value ?? false),
	variantCount: z
		.number()
		.nullish()
		.transform((value) => value ?? 0),
	primaryImage: z
		.string()
		.nullish()
		.transform((value) => value ?? undefined),
});

/** `GET /catalog/products` sends the search result itself as `data`. */
export const productSearchResultSchema = z.looseObject({
	products: z.array(searchedProductSchema),
	total: z.number(),
	page: z.number(),
	totalPages: z.number(),
	facets: facetsSchema.optional(),
}) satisfies z.ZodType<ProductSearchResult>;

/**
 * `GET /catalog/products/:id`. `product` is the row; everything else is a
 * SIBLING of it, which is exactly the shape the caller flattens.
 */
export const productDetailPayloadSchema = z.looseObject({
	product: publicProductSchema,
	brand: publicBrandSchema.nullish(),
	category: publicCategorySchema.nullish(),
	variants: z.array(publicVariantSchema).nullish(),
	price: publicPriceSchema.nullish(),
	prices: z.array(publicPriceSchema).nullish(),
	sourceable: z.boolean().nullish(),
	unsourceableReason: z.string().nullish(),
	specTemplate: publicSpecTemplateSchema.nullish(),
});

/** `GET /catalog/products/:id/variants` sends `{ variants }`. */
export const variantListSchema = z.looseObject({
	variants: lenientList(publicVariantSchema),
});

/** `GET /catalog/compare` sends `{ products: [{ product, ...siblings }] }`. */
export const compareSchema = z.looseObject({
	products: lenientList(
		z.looseObject({
			product: publicProductSchema,
			// `null` on the wire, `undefined` in the declared type.
			brand: publicBrandSchema.nullish().transform((v) => v ?? undefined),
			category: publicCategorySchema.nullish().transform((v) => v ?? undefined),
			variants: z
				.array(publicVariantSchema)
				.nullish()
				.transform((v) => v ?? undefined),
			price: publicPriceSchema.nullish(),
			prices: z.array(publicPriceSchema).nullish(),
		}),
	),
});
